import { normalizeDateStartInput } from "~/utils/isoWeek";
import {
  formatWorkoutIsoWeekRangeLabel,
  formatWorkoutScheduleDayUnknownLabel,
  isWorkoutCalendarDayKnown,
} from "~/utils/workoutScheduleContext";

function parseIsoDate(value: string | null | undefined): Date | null {
  const normalized = normalizeDateStartInput(value);
  if (!normalized) {
    return null;
  }
  const [year, month, day] = normalized.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatUtcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function capitalizeFirst(value: string): string {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function formatTrainingWeekdayLabel(
  dateIso: string | null | undefined,
  weekdayFromApi?: string | null,
): string {
  const fromApi = String(weekdayFromApi || "").trim();
  if (fromApi) {
    return capitalizeFirst(fromApi);
  }
  const date = parseIsoDate(dateIso);
  if (!date) {
    return "";
  }
  return capitalizeFirst(
    new Intl.DateTimeFormat("ru-RU", { weekday: "long", timeZone: "UTC" }).format(date),
  );
}

export function formatTrainingDateShortLabel(dateIso: string | null | undefined): string {
  const date = parseIsoDate(dateIso);
  if (!date) {
    return "";
  }
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(date);
}

/** сегодня / вчера / завтра / N дней назад / через N дней */
export function formatTrainingDateRelativeLabel(
  dateIso: string | null | undefined,
  todayIso = formatUtcToday(),
): string {
  const date = normalizeDateStartInput(dateIso);
  const today = normalizeDateStartInput(todayIso);
  if (!date || !today) {
    return "";
  }

  const diffDays = Math.round(
    (parseIsoDate(date)!.getTime() - parseIsoDate(today)!.getTime()) / 86400000,
  );

  if (diffDays === 0) {
    return "сегодня";
  }
  if (diffDays === -1) {
    return "вчера";
  }
  if (diffDays === 1) {
    return "завтра";
  }

  const abs = Math.abs(diffDays);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  let word = "дней";
  if (mod10 === 1 && mod100 !== 11) {
    word = "день";
  } else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    word = "дня";
  }

  if (diffDays < 0) {
    return `${abs} ${word} назад`;
  }
  return `через ${abs} ${word}`;
}

/** «29 июн пн» — шапка экрана записи тренировки (/session). */
export function formatWorkoutSessionHeaderDateLabel(
  dateIso: string | null | undefined,
  weekdayFromApi?: string | null,
): string {
  const date = parseIsoDate(dateIso);
  if (!date) {
    return String(dateIso || "").trim();
  }

  const day = date.getUTCDate();
  const month = new Intl.DateTimeFormat("ru-RU", {
    month: "short",
    timeZone: "UTC",
  })
    .format(date)
    .replace(/\.$/, "")
    .toLowerCase();
  const weekday = formatTrainingWeekdayShortLabel(dateIso, weekdayFromApi).replace(
    /\.$/,
    "",
  ).toLowerCase();

  return `${day} ${month} ${weekday}`;
}

export function formatTrainingWeekdayShortLabel(
  dateIso: string | null | undefined,
  weekdayFromApi?: string | null,
): string {
  const fromApi = String(weekdayFromApi || "").trim();
  if (fromApi) {
    const short = fromApi.slice(0, 2).toLowerCase();
    return short ? `${short}.` : "";
  }
  const date = parseIsoDate(dateIso);
  if (!date) {
    return "";
  }
  const short = new Intl.DateTimeFormat("ru-RU", {
    weekday: "short",
    timeZone: "UTC",
  }).format(date);
  const normalized = short.replace(/\.$/, "").toLowerCase();
  return normalized ? `${normalized}.` : "";
}

export function formatTrainingDayScheduleMetaPlate(day: {
  training_date?: string | null;
  weekday?: string | null;
  schedule_context?: {
    schedule_mode?: string | null;
    effective_date?: string | null;
    calendar_day_known?: boolean | null;
    iso_week_label?: string | null;
    schedule_day_hint?: string | null;
    iso_year?: number | null;
    iso_week?: number | null;
    timezone_context?: {
      viewer?: { local_date?: string | null } | null;
    } | null;
  } | null;
} | null | undefined): { primary: string; secondary: string } {
  if (!isWorkoutCalendarDayKnown(day?.schedule_context)) {
    return {
      primary: formatWorkoutIsoWeekRangeLabel(day?.schedule_context) || "выбрать",
      secondary: "",
    };
  }

  const viewerDate = normalizeDateStartInput(day?.schedule_context?.timezone_context?.viewer?.local_date);
  const derivedDate = day?.schedule_context?.schedule_mode === "derived"
    ? normalizeDateStartInput(day?.schedule_context?.effective_date)
    : "";
  const dateIso = viewerDate || derivedDate || normalizeDateStartInput(day?.training_date);
  if (!dateIso) {
    return { primary: "выбрать", secondary: "" };
  }

  const primary = formatTrainingDateShortLabel(dateIso) || "выбрать";
  const weekdayShort = formatTrainingWeekdayShortLabel(dateIso, day?.weekday);
  const relative = formatTrainingDateRelativeLabel(dateIso);
  const secondaryParts = [weekdayShort, relative].filter(Boolean);

  return {
    primary,
    secondary: secondaryParts.length ? `(${secondaryParts.join(", ")})` : "",
  };
}

/** «Понедельник, 6 мая (2 дня назад)» */
export function formatTrainingDayScheduleLabel(day: {
  training_date?: string | null;
  weekday?: string | null;
  schedule_context?: {
    schedule_mode?: string | null;
    effective_date?: string | null;
    calendar_day_known?: boolean | null;
    iso_week_label?: string | null;
    schedule_day_hint?: string | null;
    iso_year?: number | null;
    iso_week?: number | null;
    timezone_context?: {
      viewer?: { local_date?: string | null } | null;
    } | null;
  } | null;
} | null | undefined, todayIso = formatUtcToday()): string {
  if (!isWorkoutCalendarDayKnown(day?.schedule_context)) {
    return formatWorkoutScheduleDayUnknownLabel(day?.schedule_context);
  }

  const viewerDate = normalizeDateStartInput(day?.schedule_context?.timezone_context?.viewer?.local_date);
  const derivedDate = day?.schedule_context?.schedule_mode === "derived"
    ? normalizeDateStartInput(day?.schedule_context?.effective_date)
    : "";
  const dateIso = viewerDate || derivedDate || normalizeDateStartInput(day?.training_date);
  if (!dateIso) {
    return "";
  }

  const weekday = formatTrainingWeekdayLabel(dateIso, day?.weekday);
  const dateLabel = formatTrainingDateShortLabel(dateIso);
  const relative = formatTrainingDateRelativeLabel(dateIso, todayIso);

  const head = [weekday, dateLabel].filter(Boolean).join(", ");
  return relative ? `${head} (${relative})` : head;
}
